import React, { Component, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import styled from "styled-components";
import { colors, Button, Tooltip, InfoIcon, fontFamilies, ConnectedPlusIcon, IconButton, AddIcon, SkubotSpinner, Text, AlertNotification } from "@commonsku/styles";

import ProjectMarginSummary from './ProjectMarginSummary';

import { createAddCallout } from '../actions/callout';
import { createLoadOrder } from '../actions/order';
import { updatePurchaseOrder } from '../actions/purchase_order';
import { getBillingBySalesOrder } from '../selectors';
import { formatMoneyCurrency, formatMoney, isNumeric } from '../utils';
import VendorCreditPopup from "./vendor-credit/VendorCreditPopup";
import { retrieveVendorCredit } from "../redux/vendor_credits";
import { getPromostandardsInvoices } from '../redux/promostandardsInvoices';
import BillPopup from './bills/BillPopup';
import { getDefaultBill, retrieveBill, saveBill } from '../redux/bills';
import { useIdentity } from '../hooks';
import { useProjectBillSave } from './bills/useProjectBillSave';
import { trackEvent } from '../utils/analytics';

const VendorCreditBadgeContainer = styled.div`
&&& {
  background: ${colors.secondary3['10']};
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  width: fit-content;
  padding: 0.5rem;
}
`;

const VendorCreditTitle = styled.div`
  color: ${colors.primary1.main};
`;

function VendorCreditBadge({ vendorCredit }) {
  const [details, setDetails] = useState();
  const dispatch = useDispatch();

  const handleClick = async () => {
    const details = await dispatch(retrieveVendorCredit(vendorCredit.vendor_credit_id));
    setDetails(details);
  };

  const handleClose = () => setDetails();

  return (
    <>
      {details && <VendorCreditPopup vendorCredit={details} onClose={handleClose} isDisabled={true} />}
      <VendorCreditBadgeContainer onClick={handleClick}>
        <VendorCreditTitle>Vendor Credit {vendorCredit.form_number}</VendorCreditTitle>
        <div>{formatMoneyCurrency(-vendorCredit.total_subtotal, vendorCredit.currency_id)}</div>
      </VendorCreditBadgeContainer>
    </>
  );
}

const BillRow = ({ bill, canCreateBill, onUpdate, canEdit, setBill, isMultiple }) => {
  const dispatch = useDispatch();

  const [confirmLoading, setConfirmLoading] = useState(false);
  const identity = useIdentity();
  const projectBillSave = useProjectBillSave();
  const isProjectBillsPage = true;

  const onCreateBill = async (bill, isReview = false) => {
    try {
      const tempBill = await getDefaultBill([bill.purchase_order_id], isProjectBillsPage);
      setBill(tempBill, isReview);
    } catch (e) {
      dispatch(createAddCallout('error', 'Unable to create bill from purchase order', 'error'));
    }
  };

  const hasPO = !!bill.purchase_order_id;
  const hasBill = bill.bill_reference_number !== null;
  const addBill = !hasBill && canCreateBill && 0 == bill.billed;
  const bill_subtotal = formatMoneyCurrency(bill.bill_subtotal, bill.bill_currency);
  const bill_item_subtotal = formatMoneyCurrency(bill.bill_item_subtotal, bill.bill_currency);
  const isBilled = 1 == bill.billed;

  const getInvoiceStatusAndColorAndTooltipText = (is_epo_connected, is_epo_success, ps_invoices_status) => {
    if (!is_epo_connected) {
      return [
        'Not Available',
        colors.neutrals['60'],
        'This PO is not for a Connected Supplier, so you have to enter it manually.'
      ];
    }
    if (!is_epo_success) {
      return [
        'PO Not Submitted',
        colors.neutrals['60'],
        'Awaiting PO submission to begin matching'
      ];
    }

    const statusMap = {
      MATCHED: [
        '100%',
        colors.secondary3.main,
        'The quantities and subtotal are the same on the supplier bill and your PO.'
      ],
      UNMATCHED: [
        'Not Matching',
        colors.secondary2.dark,
        'The quantities and subtotal are not the same on the supplier bill and your PO.'
      ],
      FEE_UNMATCHED: [
        'Fee Unmatched',
        colors.secondary2.dark,
        'The quantities and subtotal are the same on the supplier bill and your PO, except for an extra charge.'
      ],
      PENDING: [
        'Expecting Bill',
        colors.neutrals['60'],
        'We are expecting to receive a bill from this Connected supplier. It will appear here shortly.'
      ]
    };

    return statusMap[ps_invoices_status] || statusMap.PENDING;
  };

  const getMatchStatus = (rowBill, hasBill) => {
    if (hasBill || 1 == rowBill.billed) return null;

    const is_epo_success = rowBill.is_epo_success;
    const is_epo_connected = rowBill.is_epo_connected;
    const match_status = rowBill.ps_invoices_status;
    const [text, color, tooltip] = getInvoiceStatusAndColorAndTooltipText(is_epo_connected, is_epo_success, match_status);

    return (
      <>
        <div data-tooltip-id={`match-tooltip-${rowBill.purchase_order_id}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {is_epo_connected && <ConnectedPlusIcon color={color} />}
          <div style={{ fontWeight: 600, fontFamily: fontFamilies.regular, color, lineHeight: '24px' }}>{text}</div>
        </div>
        <Tooltip id={`match-tooltip-${rowBill.purchase_order_id}`} clickable place='left' style={{ backgroundColor: "rgba(18, 57, 82, 0.9)", padding: '13px 16px 13px 16px', borderRadius: 5, fontSize: 16, zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: 10 }} arrowColor='rgba(18, 57, 82, 0.9)'>
          <div>{tooltip}</div>
          {
            is_epo_connected && is_epo_success && match_status === 'PENDING' && <Button
              size='small'
              onClick={() => {
                dispatch(getPromostandardsInvoices(rowBill.purchase_order_id));
              }}>
              Refresh
            </Button>
          }
        </Tooltip>
      </>
    );
  };

  const renderBillingActions = (rowBill, hasBill, addBill, onCreateBill, isMultiple) => {
    const onConfirmBill = async () => {
      setConfirmLoading(true);
      try {
        const tempBill = await getDefaultBill([rowBill.purchase_order_id], isProjectBillsPage);
        const savedBill = await dispatch(saveBill(tempBill));
        await projectBillSave({
          isNew: true,
          isReview: true,
          isDirty: false,
          bill: savedBill
        });
      } catch (error) {
        dispatch(createAddCallout('error', 'Unable to confirm bill', 'error'));
      } finally {
        setConfirmLoading(false);
      }
    };
    const actions = {
      matched: () => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 6 }}>
          {confirmLoading ? <IconButton variant='disabled' size='small' style={{ width: 48 }} Icon={SkubotSpinner({ skubot: false, size: 'button', spinnerSize: 16, color: '#3A5F76' })} /> :
            <IconButton
              variant='custom'
              size='small'
              iconPosition='left'
              variantBorderColor='transparent'
              variantBg={colors.secondary3.main}
              variantColor={colors.primary2.main}
              variantHoverBorderColor={colors.secondary3['70']}
              variantHoverBg={colors.secondary3['70']}
              Icon={ConnectedPlusIcon}
              onClick={onConfirmBill}
            >
              Confirm Bill
            </IconButton>
          }
          <Button
            variant='text'
            size='small'
            onClick={() => {
              onCreateBill(rowBill, true);
              if (identity) {
                trackEvent('Bill - Review', {
                  tenant_id: identity.tenant_id,
                  login_email: identity.login_email,
                });
              }
            }}
          >
            Review Bill
          </Button>
        </div>
      ),

      billLink: () => (
        <Text
          style={{ cursor: 'pointer', color: colors.primary1['65'], fontFamily: fontFamilies.regular, display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle' }}
          onClick={async () => {
            const retrievedBill = await retrieveBill(rowBill.bill_id)(dispatch);
            setBill(retrievedBill);
          }}>
          {rowBill.ps_invoices_confirmed ? <ConnectedPlusIcon /> : null}
          {rowBill.bill_reference_number}
        </Text>
      ),

      createBill: () => (
        <IconButton
          variant='primary'
          size='small'
          iconPosition='left'
          Icon={AddIcon}
          onClick={e => {
            e.preventDefault();
            onCreateBill(rowBill);
          }}
        >
          Add Bill
        </IconButton>
      )
    };

    if (hasBill) return actions.billLink();
    if (rowBill.ps_invoices_status === 'MATCHED' && !isMultiple && 1 != rowBill.billed) return actions.matched();
    if (addBill && 1 != rowBill.billed) return actions.createBill();

    return null;
  };

  return (
    <>
      <tr>
        <td>{hasPO && <a target="_blank" href={`/purchase_order.php?id=${bill.purchase_order_id}`} rel="noreferrer">{bill.form_number}</a>}</td>
        <td>{bill.po_status_name ? <span style={{ padding: '0.4rem' }} className={'status-badge ' + bill.po_status_name.replace(/ /g, '-').toLowerCase()}>{bill.po_status_name}</span> : <span>&nbsp;</span>}</td>
        <td>{hasPO ? <a target="_blank" href={`/vendor.php?id=${bill.po_company_id}&division_id=${bill.po_division_id}`} rel="noreferrer">{bill.po_division_name}</a> : <span>&nbsp;</span>}</td>
        <td>{isNumeric(bill.total) ? '$' + formatMoney(bill.total) : <span>&nbsp;</span>}</td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 6 }}>
            {isMultiple ? null : getMatchStatus(bill, hasBill)}
          </div>
        </td>
        <td>
          {renderBillingActions(bill, hasBill, addBill, onCreateBill, isMultiple)}
        </td>
        <td></td>
        <td>{hasBill ? <a target="_blank" href={`/vendor.php?id=${bill.bill_company_id}&division_id=${bill.bill_division_id}`} rel="noreferrer">{bill.bill_division_name}</a> : <span>&nbsp;</span>}</td>
        <td style={{ textAlign: 'center', width: '1%' }}>
          {hasPO && (onUpdate ? <input type="checkbox" disabled={!canEdit} checked={isBilled} onChange={e => onUpdate(bill.purchase_order_id, 'billed', bill.billed)(isBilled ? 0 : 1)} /> : (isBilled ? 'Yes' : 'No'))}
        </td>
        <td>{bill.bill_subtotal !== null ?
          <>
            {bill_subtotal}
            {bill_subtotal !== bill_item_subtotal && <>
              <br />({bill_item_subtotal} on project)
            </>}
          </> : <span>&nbsp;</span>}
        </td>
      </tr>
      {bill.vendor_credits.length > 0 && (
        <tr align="right">
          <td colspan="8">
            {bill.vendor_credits.map(
              (vc) => <VendorCreditBadge vendorCredit={vc} />
            )}
          </td>
        </tr>
      )}
    </>
  );
};

class ProjectBilling extends Component {

  constructor(props) {
    super(props);

    this.hasCapability = this.hasCapability.bind(this);
    this.state = {
      bill: null,
      isReview: false,
      loading: true
    };
  }

  componentDidMount() {
    const purchase_order_ids = this.props.bills_by_sales_order
      .flatMap(order => (order.bills || []))
      .filter(bill => bill.is_epo_success && bill.ps_invoices_confirmed !== 1)
      .map(bill => bill.purchase_order_id)
      .filter(Boolean);
    this.fetchPromostandardsInvoices(purchase_order_ids);
  }

  UNSAFE_componentWillMount() {
    this.setState({ loading: true });
    const sales_orders = this.props.project.sales_orders || [];
    const invoices = this.props.project.invoices || [];

    Promise.all([
      ...sales_orders.map(o => this.props.loadOrder(o.order_id, true)),
      ...invoices.map(o => this.props.loadOrder(o.order_id, true)),
    ]).finally(() => this.setState({ loading: false }));
  }

  hasCapability(capability) {
    return this.props.identity.capabilities.includes(capability);
  }

  hasUserFlag(flag_name) {
    return this.props.identity.user_flags.includes(flag_name);
  }

  renderLoading() {
    return (
      <div className="main-content project-order" style={{ textAlign: 'center' }} >
        <img style={{ marginTop: '2rem' }} src="/images/gears.gif" />
      </div>
    );
  }

  renderPendingPayment(message) {
    return (
      <div className="main-content project-order">
        <AlertNotification alertType="error">
          {message ? message : <>Order can not be loaded. Please check back later.</>}
        </AlertNotification>
      </div>
    );
  }

  fetchPromostandardsInvoices(purchase_order_ids) {
    this.props.fetchPromostandardsInvoices(purchase_order_ids);
  }

  render() {
    const { project, loaded } = this.props;
    const { loading } = this.state;

    if (!loaded || loading) {
      if (project.sales_orders.length && project.sales_orders[0].hasError) {
        return this.renderPendingPayment(project.sales_orders[0].errorMessage);
      }
      return this.renderLoading();
    }

    const { bills_by_sales_order, onUpdatePurchaseOrder } = this.props;
    const canCreateBill = this.hasCapability('CREATE-BILL');
    const canUpdatePurchaseOrder = this.hasCapability('MODIFY-PURCHASE-ORDER');
    const canEdit = this.hasCapability('MODIFY-BILL');

    return (
      <div className="main-content">
        {bills_by_sales_order.map((order, i) => {
          const subtotals_by_currency = {};
          order.bills.filter(b => !!b.bill_id).forEach(
            b => {
              const bill_subtotal = subtotals_by_currency[b.bill_currency] ?? { currency: b.bill_currency, exchange_rate: b.bill_exchange_rate, bill_subtotal: 0, bill_item_subtotal: 0, vendor_credit_subtotal: 0 };
              bill_subtotal.bill_subtotal += Number(b.bill_subtotal);
              bill_subtotal.bill_item_subtotal += Number(b.bill_item_subtotal);
              subtotals_by_currency[b.bill_currency] = bill_subtotal;
              b.vendor_credits.forEach(
                vc => {
                  const vc_subtotal = subtotals_by_currency[vc.currency_id] ?? { currency: vc.currency_id, exchange_rate: 1, bill_subtotal: 0, bill_item_subtotal: 0, vendor_credit_subtotal: 0 };
                  vc_subtotal.vendor_credit_subtotal += vc.total_subtotal;
                  subtotals_by_currency[vc.currency_id] = vc_subtotal;
                }
              );
            }
          );
          const subtotals = Object.values(subtotals_by_currency);
          const purchase_order_ids = order.bills
            .filter(b => b.purchase_order_id)
            .reduce((acc, b) => {
              acc[b.purchase_order_id] = (acc[b.purchase_order_id] || 0) + 1;
              return acc;
            }, {});

          return (
            <div key={i}>
              <div className="small-12 columns">
                <h3>For Sales Order #{order.form_number}</h3>
              </div>
              <div className="small-12 columns">
                <table id="bills-table">
                  <thead>
                    <tr>
                      <th>PO #</th>
                      <th>PO Status</th>
                      <th>PO Supplier</th>
                      <th>PO Amount</th>
                      <th><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <Tooltip
                          id={`match-tooltip`}
                          place='top'
                          style={{ backgroundColor: "rgba(18, 57, 82, 0.9)", padding: '13px 16px 13px 16px', borderRadius: 5, fontSize: 16, zIndex: 1 }}
                          arrowColor='rgba(18, 57, 82, 0.9)'
                        />
                        <div>Match</div>
                        <InfoIcon altText='' color={colors.primary1.main} data-tooltip-id={`match-tooltip`} data-tooltip-content={'Does the supplier invoice match your PO?'} />
                      </div>
                      </th>
                      <th>Bill</th>
                      <th></th>
                      <th>Bill Supplier</th>
                      <th>All Bills Received</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.bills.map((b, index) => {
                      const isMultiple = b.purchase_order_id && purchase_order_ids[b.purchase_order_id] > 1;
                      return (
                        <BillRow
                          key={index}
                          bill={b}
                          canCreateBill={canCreateBill}
                          onUpdate={canUpdatePurchaseOrder ? onUpdatePurchaseOrder : null}
                          canEdit={canEdit}
                          setBill={(bill, isReview = false) => this.setState({ bill, isReview })}
                          isMultiple={isMultiple}
                        />
                      );
                    }
                    )}
                  </tbody>
                </table>
              </div>
              <ProjectMarginSummary project={project} order_id={order.order_id} force_subtotal={true} />
              <div className="large-4 columns">&nbsp;</div>
              <div className="large-4 columns">
                <strong>Bill Subtotal:</strong>
                {subtotals.map(
                  subtotal => (
                    <p key={subtotal.currency}>
                      {formatMoneyCurrency(subtotal.bill_subtotal - subtotal.vendor_credit_subtotal, subtotal.currency)}
                      {formatMoneyCurrency(subtotal.bill_subtotal, subtotal.currency) !== formatMoneyCurrency(subtotal.bill_item_subtotal, subtotal.currency) && (
                        <>
                          <br />
                          ({formatMoneyCurrency(subtotal.bill_item_subtotal - subtotal.vendor_credit_subtotal, subtotal.currency)} on project)
                        </>
                      )}
                    </p>
                  )
                )}
              </div>
            </div>
          );
        })}
        {this.state.bill &&
          <ProjectBillPopupWrapper
            isReview={this.state.isReview}
            bill={this.state.bill}
            onClose={() => { this.setState({ bill: null }); }}
          />}
      </div>
    );
  }
}

const ProjectBillPopupWrapper = ({ bill, isReview, onClose }) => {
  const handleProjectBillSave = useProjectBillSave();

  return (
    <BillPopup
      bill={bill}
      isReview={isReview}
      onClose={onClose}
      onSave={handleProjectBillSave}
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const sales_orders = ownProps.project.sales_orders || [];
  const sales_order = sales_orders.length > 0 ? sales_orders[0] : null;
  const loaded = sales_order?.loaded ?? false;
  const margin_data = sales_order ? { [sales_order.order_id]: sales_order.margin_data } : {};
  const project = { ...ownProps.project, margin_data };
  const bills_by_sales_order = getBillingBySalesOrder(state);
  return {
    loaded,
    project,
    bills_by_sales_order,
    identity: state.identity
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    loadOrder: (order_id, force = false) => dispatch(createLoadOrder(order_id, force)),
    onUpdatePurchaseOrder: (purchase_order_id, field, previous_value) => value => {
      dispatch(updatePurchaseOrder(purchase_order_id, field, previous_value, value));
    },
    fetchPromostandardsInvoices: purchase_order_ids => dispatch(getPromostandardsInvoices(purchase_order_ids)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectBilling);
